import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Grid2,
  Box,
} from '@mui/material';
import StatusChip from '../../customTableComponent/statusChip';

import EditButton from '../../../assets/SVG/editPencil.svg';
import viewIcon from '../../../assets/SVG/eye_view.svg';
import PrescribedUnavailable from '../prescribedUnavailable';
import { getSurgicalSpermRetrievalList } from './spermRetrieval.helper';
import { usePatientContext } from '../../SubPatinetProvider';
import { ProcedureSummary } from '../laparoscopy/laparoscopy.helper';

const SurgicalSpermRetrievalList = ({
  onEdit,
  onView,
}: {
  onEdit: (id: number) => void;
  onView: (id: number) => void;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [surgicalSpermRetrievalDetails, setSurgicalSpermRetrievalDetails] = useState<any[]>([]);
  const [procedureSummary, setProcedureSummary] = useState<ProcedureSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const { patientId } = usePatientContext();

  useEffect(() => {
    // Fetch surgicalSpermRetrieval details
    getSurgicalSpermRetrievalList(patientId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        setSurgicalSpermRetrievalDetails(response.objects);
        setProcedureSummary(response.procedure_summary);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching surgicalSpermRetrieval details:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [patientId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }


  if (!surgicalSpermRetrievalDetails.length) {
    return <PrescribedUnavailable />; // Show PrescribedUnavailable when no data is available
  }
  
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                height: '2rem',
                background: 'rgba(250, 250, 250, 1)',
                '& .MuiTableCell-root': {
                  padding: '0.6rem 0.6rem',
                },
              }}
            >
              {['Date of Procedure', 'Procedure ID', 'Surgeon Name', 'Status', ''].map(
                (title, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.45',
                      textAlign: 'left',
                      color: 'rgba(35, 35, 35, 1)',
                    }}
                  >
                    {title}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {surgicalSpermRetrievalDetails.map((surgicalSpermRetrieval) => (
              <TableRow key={surgicalSpermRetrieval.id}>
                <TableCell>
                  {new Date(surgicalSpermRetrieval.created_on).toLocaleDateString('en-US')}
                </TableCell>
                <TableCell>{surgicalSpermRetrieval?.services[0]?.code}</TableCell>
                <TableCell>{surgicalSpermRetrieval?.doctor_name}</TableCell>
                <TableCell>
                  <StatusChip
                    value={surgicalSpermRetrieval?.sperm_retrieval_status ? 'Completed' : 'Pending'}
                  />
                </TableCell>

                <TableCell>
                  {surgicalSpermRetrieval?.sperm_retrieval_status ? (
                    <Tooltip title="View Details">
                      <img 
                        src={viewIcon} 
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }} 
                        alt="View Icon" 
                        onClick={() => onView(surgicalSpermRetrieval.id)} 
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Edit Details">
                      <img 
                        src={EditButton} 
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }} 
                        alt="Edit Icon" 
                        onClick={() => onEdit(surgicalSpermRetrieval.id)} 
                      />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
  
      {procedureSummary && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: '300px',
            width: 'calc(100% - 300px)',
            px: 4,
            py: 2,
            pb: 4,
            backgroundColor: '#fff',
            zIndex: 10,
          }}
        >
          <Grid2 container alignItems="center" justifyContent="space-between">
            <Grid2>
              <span style={{ fontWeight: 600, fontSize: '16px' }}>
                Total Count :{' '}
                <span style={{ fontWeight: 500 }}>
                  {procedureSummary.sperm_retrieval.completed + procedureSummary.sperm_retrieval.pending}
                </span>
              </span>
            </Grid2>

            <Grid2 className="d-flex justify-content-end">
              {[
                {
                  color: '#47B35F',
                  label: 'Complete',
                  count: procedureSummary.sperm_retrieval.completed,
                },
                {
                  color: '#EFAE78',
                  label: 'Pending',
                  count: procedureSummary.sperm_retrieval.pending,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="d-flex align-items-center"
                  style={{ marginRight: '24px', minWidth: '100px' }}
                >
                  <div
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      marginRight: '8px',
                    }}
                  ></div>
                  <span style={{ fontWeight: 500, fontSize: '14px' }}>
                    {item.label}{' '}
                    <span style={{ fontWeight: 600 }}>
                      {item.count.toString().padStart(2, '0')}
                    </span>
                  </span>
                </div>
              ))}
            </Grid2>
          </Grid2>
        </Box>
      )}
    </>
  );
};

export default SurgicalSpermRetrievalList;
